import { Suspense, lazy, useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import SiteNav from "@/components/SiteNav";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Manifesto from "@/components/Manifesto";
import Showcase from "@/components/Showcase";
import CapabilityIndex from "@/components/home/CapabilityIndex";
import WhyElentara from "@/components/home/WhyElentara";
import HowWeWork from "@/components/home/HowWeWork";
import ExperimentsPreview from "@/components/home/ExperimentsPreview";
import WhoWeWorkWith from "@/components/home/WhoWeWorkWith";
import FinalCTA from "@/components/FinalCTA";
import SiteFooter from "@/components/SiteFooter";
import Loader from "@/components/Loader";
import CustomCursor from "@/components/CustomCursor";
import StaticBackdrop from "@/components/StaticBackdrop";
import SEO from "@/components/SEO";
import About from "@/pages/About";
import CaseStudy from "@/pages/CaseStudy";
import Work from "@/pages/Work";
import Capabilities from "@/pages/Capabilities";
import CapabilityDetail from "@/pages/CapabilityDetail";
import Experiments from "@/pages/Experiments";
import ExperimentDetail from "@/pages/ExperimentDetail";
import StartProject from "@/pages/StartProject";
import NotFound from "@/pages/NotFound";
import { SITE, url } from "@/data/site";
import { CAPABILITIES } from "@/data/capabilities";
import { track, EVENTS } from "@/lib/analytics";
import { Toaster } from "sonner";

const Scene3D = lazy(() => import("@/components/Scene3D"));

const HOME_TITLE = "Elentara | Creative Technology Studio — Immersive Web, 3D & Interactive Systems";
const HOME_DESCRIPTION =
  "Elentara is a creative technology studio building immersive web experiences, 3D and motion, spatial computing and interactive systems — digital experiences beyond the expected.";
const HOME_CANONICAL = url("/");
const HOME_IMAGE =
  "https://images.unsplash.com/photo-1671519821564-ced7e41ee7ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMDNEJTIwZmx1aWQlMjBkYXJrJTIwbmVvbiUyMGx1eHVyeSUyMGFyY2hpdGVjdHVyZSUyMGdlb21ldHJpY3xlbnwwfHx8fDE3ODg0MjAwMTl8MA&ixlib=rb-4.1.0&q=85";

const HOME_JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: "Elentara — Creative Technology Studio",
      url: HOME_CANONICAL,
      description: HOME_DESCRIPTION,
      primaryImageOfPage: HOME_IMAGE,
      isPartOf: { "@id": `${SITE.base}/#website` },
      about: { "@id": `${SITE.base}/#organization` },
      significantLink: [url("/work"), url("/capabilities"), url("/experiments")],
    },
    {
      "@type": "ItemList",
      name: "Capabilities",
      itemListElement: CAPABILITIES.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: c.name,
        url: url(`/capabilities/${c.slug}`),
      })),
    },
  ],
};

const MIN_3D_VIEWPORT_WIDTH = 768;
const LOADER_DURATION_MS = 1800;
const LENIS_LERP = 0.09;

function Home({ enable3D }) {
  useEffect(() => {
    track(EVENTS.HOME_VIEW);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `track`/`EVENTS` are module
    // imports; this must fire exactly once per home mount.
  }, []);

  return (
    <>
      <SEO
        title={HOME_TITLE}
        description={HOME_DESCRIPTION}
        canonical={HOME_CANONICAL}
        image={HOME_IMAGE}
        jsonLd={HOME_JSON_LD}
      />
      {enable3D ? (
        <Suspense fallback={<StaticBackdrop />}>
          <Scene3D />
        </Suspense>
      ) : (
        <StaticBackdrop />
      )}
      <SiteNav delay={1.7} />
      <main className="relative">
        <Hero />
        <Marquee />
        <Manifesto />
        <Showcase />
        <CapabilityIndex
          title="Engineered for the next dimension."
          lead={`${SITE.serviceLine} — five capability areas held under one creative direction.`}
        />
        <WhyElentara />
        <HowWeWork />
        <ExperimentsPreview />
        <WhoWeWorkWith />
        <FinalCTA from="home" />
      </main>
      <SiteFooter />
    </>
  );
}

function Shell({ loaded, enable3D, finePointer }) {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    window.__lenis?.scrollTo(0, { immediate: true });
  }, [pathname]);  return (
    <>
      {pathname === "/" && <Loader done={loaded} />}
      {finePointer && <CustomCursor />}
      <div className="noise-overlay" />
      <Routes>
        <Route path="/" element={<Home enable3D={enable3D} />} />
        <Route path="/about" element={<About />} />
        <Route path="/work" element={<Work />} />
        <Route path="/work/:slug" element={<CaseStudy />} />
        <Route path="/capabilities" element={<Capabilities />} />
        <Route path="/capabilities/:slug" element={<CapabilityDetail />} />
        <Route path="/experiments" element={<Experiments />} />
        <Route path="/experiments/:slug" element={<ExperimentDetail />} />
        <Route path="/start-a-project" element={<StartProject />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

function App() {
  const [loaded, setLoaded] = useState(false);
  const [enable3D, setEnable3D] = useState(false);
  const [finePointer, setFinePointer] = useState(false);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const lowPower = navigator.hardwareConcurrency ? navigator.hardwareConcurrency <= 4 : false;
    setEnable3D(!reduced && !lowPower && window.innerWidth >= MIN_3D_VIEWPORT_WIDTH);
    setFinePointer(window.matchMedia("(pointer: fine)").matches && !reduced);

    let lenis = null;
    let raf;
    if (!reduced) {
      lenis = new Lenis({ lerp: LENIS_LERP, smoothWheel: true });
      window.__lenis = lenis;
      const loop = (time) => {
        lenis.raf(time);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    }
    const timer = setTimeout(() => setLoaded(true), LOADER_DURATION_MS);
    return () => {
      clearTimeout(timer);
      if (lenis) {
        cancelAnimationFrame(raf);
        lenis.destroy();
        window.__lenis = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-time bootstrap: capability
    // detection, the Lenis raf loop and the loader timer must run once on mount only.
    // Everything referenced is a module-level constant, an import or a stable setState.
  }, []);

  return (
    <div className="App" data-testid="elentara-app">
      <BrowserRouter>
        <Shell loaded={loaded} enable3D={enable3D} finePointer={finePointer} />
      </BrowserRouter>
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}

export default App;
